import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

const faqData = [
  {
    q: "Quand faut-il remplacer un double vitrage à Lyon ?",
    a: "Un double vitrage doit être remplacé lorsqu'il est cassé, lorsque de la condensation apparaît entre les deux verres (le joint d'étanchéité est HS), ou lorsqu'il n'isole plus correctement. Un vitrage de plus de 20 ans peut avantageusement être remplacé par un modèle VIR plus performant.",
  },
  {
    q: "Le remplacement du double vitrage améliore-t-il l'isolation ?",
    a: "Oui, remplacer un ancien double vitrage par un vitrage isolant renforcé (VIR) moderne améliore significativement l'isolation thermique et acoustique de votre logement. Cela se traduit par des économies d'énergie et un meilleur confort.",
  },
  {
    q: "Faut-il changer toute la fenêtre ou seulement le vitrage ?",
    a: "Dans la grande majorité des cas, seul le vitrage doit être remplacé, pas la fenêtre entière. Nous déposons l'ancien vitrage, nettoyons la feuillure et posons un vitrage neuf aux dimensions exactes. C'est plus économique et tout aussi efficace.",
  },
];

export default function RemplacementDoubleVitrage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Remplacement de double vitrage à Lyon',
    provider: { '@type': 'HomeAndConstructionBusiness', name: 'DFPRO Lyon', telephone: '+33980803684' },
    areaServed: [{ '@type': 'City', name: 'Lyon' }, { '@type': 'AdministrativeArea', name: 'Métropole de Lyon' }],
    description: 'Remplacement de double vitrage à Lyon. Vitrage isolant, mesures précises, pose soignée. Devis gratuit, garantie 10 ans.',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://depanfenetres-lyon.fr/' },
      { '@type': 'ListItem', position: 2, name: 'Vitrier à Lyon', item: 'https://depanfenetres-lyon.fr/vitrier-lyon/' },
      { '@type': 'ListItem', position: 3, name: 'Remplacement double vitrage', item: 'https://depanfenetres-lyon.fr/remplacement-double-vitrage-lyon/' },
    ],
  };

  return (
    <>
      <SEOHead
        title="Remplacement de double vitrage à Lyon | DFPRO"
        description="Double vitrage à changer à Lyon ? Vitrage isolant, mesures précises, pose soignée. Économies d'énergie, devis gratuit, garantie 10 ans. DFPRO Lyon."
        canonicalUrl="https://depanfenetres-lyon.fr/remplacement-double-vitrage-lyon/"
        ogTitle="Remplacement de double vitrage à Lyon — Isolation & confort | DFPRO"
        ogDescription="Double vitrage cassé, embué ou ancien ? Remplacement vitrage isolant à Lyon. Devis gratuit, garantie 10 ans."
        jsonLd={{ ...serviceJsonLd, ...faqJsonLd, ...breadcrumbJsonLd }}
      />

      <Header />

      <main>
        <div className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-secondary-400">
              <a href="/" className="hover:text-primary-500 transition-colors">Accueil</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <a href="/vitrier-lyon/" className="hover:text-primary-500 transition-colors">Vitrier à Lyon</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">Remplacement double vitrage</span>
            </nav>
          </div>
        </div>

        <section className="relative w-full min-h-[380px] md:min-h-[420px] flex items-center bg-background-50">
          <div className="absolute inset-0">
            <img
              src="/images/double-vitrage-hero-2026.jpg"
              alt="Remplacement de double vitrage à Lyon par DFPRO Lyon — technicien posant un vitrage isolant neuf"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/50 via-foreground-900/35 to-foreground-900/50"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-14 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 leading-tight">
                Remplacement de double vitrage à Lyon
              </h1>
              <p className="mt-4 md:mt-5 text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto leading-relaxed">
                Votre double vitrage est cassé, embué ou ne vous isole plus correctement ? Nos vitriers le remplacent rapidement à Lyon et dans la métropole. Mesures précises et pose soignée.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="tel:+33980803684" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(242,140,40,0.4)]">
                  <i className="ri-phone-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  Appeler — 09 80 80 36 84
                </a>
              </div>
            </div>
          </div>
        </section>

        <ReassuranceBar />

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              <div className="lg:col-span-2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-800 mb-5">
                  Remplacement de double vitrage à Lyon : plus de confort, moins de déperditions
                </h2>
                <div className="space-y-4 text-sm md:text-base text-secondary-500 leading-relaxed">
                  <p>
                    Le double vitrage équipe la quasi-totalité des fenêtres modernes. Composé de deux feuilles de verre séparées par une lame d'air ou de gaz (argon), il assure l'isolation thermique et acoustique de votre logement. Mais quand il est défaillant — vitre intérieure ou extérieure cassée, condensation persistante entre les verres, ou simple vieillissement — <strong className="text-foreground-800">ses performances s'effondrent</strong>.
                  </p>
                  <p>
                    Chez DFPRO Lyon, nous remplaçons votre double vitrage défectueux sur tous types de menuiseries. Nous intervenons sur <strong className="text-foreground-800">fenêtres PVC, bois et aluminium</strong>, portes-fenêtres, baies vitrées coulissantes et vérandas. Le processus est simple : notre vitrier prend les mesures exactes de votre vitrage (hauteur, largeur, épaisseur totale), commande un vitrage neuf aux dimensions, et revient pour la pose.
                  </p>
                  <p>
                    Nous proposons différents types de double vitrage selon vos besoins : <strong className="text-foreground-800">vitrage isolant standard 4/16/4</strong> (deux verres de 4 mm, lame d'air de 16 mm), <strong className="text-foreground-800">vitrage isolant renforcé (VIR)</strong> avec traitement basse émissivité pour des performances thermiques supérieures, ou vitrage acoustique pour les logements exposés au bruit. Nous vous conseillons sur le choix le plus adapté à votre situation.
                  </p>
                  <p>
                    Remplacer un double vitrage ancien par un VIR moderne peut vous faire gagner <strong className="text-foreground-800">jusqu'à 15% sur votre facture de chauffage</strong>. C'est un investissement rapidement rentabilisé, surtout dans des villes comme Lyon où les hivers peuvent être rigoureux et les étés très chauds — un bon vitrage isole dans les deux sens.
                  </p>
                  <p>
                    La pose d'un double vitrage neuf nécessite un savoir-faire précis : retrait des parcloses sans abîmer le cadre, extraction de l'ancien vitrage, nettoyage minutieux de la feuillure, calage parfait du nouveau vitrage, repose des parcloses et contrôle de l'étanchéité. Nos vitriers maîtrisent chaque étape. Toutes nos interventions à Lyon et dans la métropole bénéficient de notre <strong className="text-foreground-800">garantie 10 ans</strong>.
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6 h-fit">
                <h3 className="font-heading font-semibold text-base text-foreground-800 mb-4">Nos services vitrier</h3>
                <nav className="space-y-2">
                  {[
                    { label: 'Vitrier à Lyon (pilier)', href: '/vitrier-lyon/' },
                    { label: 'Remplacement vitre cassée', href: '/remplacement-vitre-cassee-lyon/' },
                    { label: 'Double vitrage embué', href: '/double-vitrage-embue-lyon/' },
                    { label: 'Survitrage', href: '/survitrage-lyon/' },
                    { label: 'Vitrier urgence', href: '/vitrier-urgence-lyon/' },
                  ].map((link, idx) => (
                    <a key={idx} href={link.href} className="flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-500 transition-colors py-1.5 cursor-pointer">
                      <i className="ri-arrow-right-s-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="mt-5 pt-5 border-t border-background-200">
                  <a href="tel:+33980803684" className="inline-flex items-center justify-center gap-2 w-full bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm py-3 rounded-md transition-colors cursor-pointer">
                    <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>
                    Appeler — 09 80 80 36 84
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">Questions fréquentes</h2>
            </div>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm md:text-base text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner
          title="Double vitrage à remplacer à Lyon ?"
          subtitle="Appelez le 09 80 80 36 84 pour un remplacement professionnel. Devis gratuit, garantie 10 ans."
          buttonText="Appeler maintenant"
          secondaryButtonText="Demander un devis"
          secondaryButtonHref="/contact/"
        />
      </main>
      <Footer />
    </>
  );
}